import Link from "next/link";
import { cn } from "src/utils/cn";
import { Button } from "@memento/ui/Button";
import { createContext, useContext } from "react";

// A lot of this component is managing scrolling vs. non-scrolling layout. Both
// need a margin on the right, but the scrolling layout needs to NOT add a
// margin to the scrolling content. If it does, the scroll bars will be hidden.
// That's pretty much the whole point to any of the differences between the two
// layouts.
type SectionLayoutVariant = "scrolling" | "non-scrolling";

type SectionLayoutContextProps = {
  layoutVariant: SectionLayoutVariant;
};

const SectionLayoutContext = createContext<SectionLayoutContextProps | null>(
  null
);

type SectionLayoutProviderProps = {
  children: React.ReactNode;
  variant: SectionLayoutVariant;
};

const SectionLayoutProvider = ({
  children,
  variant,
}: SectionLayoutProviderProps) => {
  return (
    <SectionLayoutContext.Provider value={{ layoutVariant: variant }}>
      {children}
    </SectionLayoutContext.Provider>
  );
};

export const useSectionLayoutProps = () => {
  const contextProps = useContext(SectionLayoutContext);

  if (!contextProps) {
    throw new Error(
      "useInputProps must be used within a SectionLayoutProvider."
    );
  }

  return contextProps;
};

const sharedSectionMargin = "lg:mr-10";
const sharedGap = "gap-3";
const sharedMobileBottomPadding = "pb-5 md:pb-0";

export function SectionLayout({
  children,
  variant,
  className,
}: {
  children: React.ReactNode;
  variant: SectionLayoutProviderProps["variant"];
  className?: string;
}) {
  return (
    <SectionLayoutProvider variant={variant}>
      <div
        className={cn(
          "flex h-full flex-col",
          sharedGap,
          variant === "non-scrolling" ? sharedSectionMargin : null,
          className
        )}
      >
        {children}
      </div>
    </SectionLayoutProvider>
  );
}

SectionLayout.H1 = function SectionLayoutH1({
  children,
}: {
  children: React.ReactNode;
}) {
  return <h1 className="font-futura text-2xl text-gray-800">{children}</h1>;
};

SectionLayout.H2 = function SectionLayoutH2({
  children,
}: {
  children: React.ReactNode;
}) {
  return <h2 className="font-futura text-xl text-gray-700">{children}</h2>;
};

SectionLayout.Header = function SectionLayoutHeader({
  heading,
  onDoneUrl,
  onClickDone = () => void 0,
  disabled = false,
  children,
}: {
  // The heading is optional because the heading might be in the children. If
  // provided, will wrap in SectionLayout.H1.
  heading?: string;
  onDoneUrl?: string;
  onClickDone?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  const { layoutVariant } = useSectionLayoutProps();

  return (
    <div
      className={cn(
        "flex items-center gap-3 md:justify-between",
        layoutVariant === "scrolling" ? sharedSectionMargin : null
      )}
    >
      <div>
        {heading ? <SectionLayout.H1>{heading}</SectionLayout.H1> : null}
        {children}
      </div>
      {onDoneUrl ? (
        <div className="hidden md:block">
          <Link href={onDoneUrl}>
            <Button
              variant="primary"
              type="button"
              width="small"
              onClick={() => onClickDone()}
              disabled={disabled}
            >
              Done
            </Button>
          </Link>
        </div>
      ) : null}
    </div>
  );
};

SectionLayout.PaddedOption = function SectionLayoutPaddedOption({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="px-2">{children}</div>;
};

type NonScrollingContentProps = {
  children: React.ReactNode;
};

SectionLayout.NonScrollingContent = function SectionLayoutNonScrollingContent({
  children,
}: NonScrollingContentProps) {
  const { layoutVariant } = useSectionLayoutProps();

  return (
    <div
      className={cn(
        "flex flex-col text-sm",
        sharedGap,
        sharedMobileBottomPadding,
        layoutVariant === "scrolling" ? sharedSectionMargin : null
      )}
    >
      {children}
    </div>
  );
};

SectionLayout.ScrollingContent = function SectionLayoutScrollingContent({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        "flex flex-col text-sm md:overflow-y-auto lg:mr-[13px] lg:pr-[13px]",
        sharedMobileBottomPadding
      )}
      style={{ ...style, scrollbarGutter: "stable" }}
    >
      {children}
    </div>
  );
};

SectionLayout.Separator = function SectionLayoutSeparator() {
  return <div className={cn("border-t-2")}></div>;
};

SectionLayout.MobileFooter = function SectionLayoutMobileFooter({
  onDoneUrl,
  onClickDone,
  className,
}: {
  onDoneUrl: string;
  onClickDone: () => void;
  className?: string;
}) {
  return (
    <Link href={onDoneUrl} className={className}>
      <Button
        variant="primary"
        type="button"
        size="lg"
        width="full"
        onClick={onClickDone}
      >
        Done
      </Button>
    </Link>
  );
};
